import { selectCurrentHotelId, selectIsInternalAdmin } from 'accessors'
import { getCampaigns, updateCampaign } from 'actions/campains'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import EditCampaignContent from 'features/ManageCampaigns/EditCampaignContent'
import EditPromoDialog from 'features/ManageCampaigns/EditPromoDialog'
import useCampaigns from 'hooks/useCampaigns'
import usePlausible from 'hooks/usePlausible'
import { campaignHierarchy } from 'main/campaignHierarchy'
import campaignNamesForCustomer, {
  campaignNamesForInternalAdmin
} from 'main/campaignNamesAndDesc'
import { DEFAULT_SUBSIDIARY_ID } from 'main/globalVariables'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'

function getCampaignName(key, isInternalAdmin) {
  return isInternalAdmin
    ? campaignNamesForInternalAdmin[key] || key
    : campaignNamesForCustomer[key] || key
}

const baseTags = [
  {
    id: '__hotelName__',
    display: 'hotelName',
    length: 40
  },
  {
    id: '__guestName__',
    display: 'firstName',
    length: 8
  },
  {
    id: '__guestLastName__',
    display: 'lastName',
    length: 8
  },
  {
    id: '__checkinDate__',
    display: 'checkinDate',
    length: 11
  },
  {
    id: '__checkoutDate__',
    display: 'checkoutDate',
    length: 11
  }
]

function getPrimaryCampaignTitle(category) {
  if (category === 'CardAbandonmentOptedIn') {
    return 'Widget welcome'
  }

  if (category === 'OptInRequest') {
    return 'Widget consent request'
  }

  return 'For regular guests'
}

function getSecondaryCampaignTitle(category) {
  if (category === 'OptInRequest') {
    return 'For guests who have already opted in'
  }

  return 'For OTA guests'
}

function getSecondaryCampaign(campaigns, category) {
  if (!campaigns || !category) {
    return null
  }

  if (category === 'OptInRequest') {
    return campaigns.find(campaign => campaign.category === 'CardAbandonmentOptedIn')
  }

  const OTAVariantCategory = campaignHierarchy?.[category]?.OTAVariant

  if (!OTAVariantCategory) {
    return null
  }

  if (category === OTAVariantCategory) {
    return null
  }

  return campaigns?.find(c => c.category === OTAVariantCategory)
}

const EditCampaign = ({ isDefaultCampaigns }) => {
  const [isLoading, setIsLoading] = useState(true)

  const dispatch = useDispatch()

  const [isEditingPromo, setIsEditingPromo] = useState(false)

  const currentSubsidiaryId = useSelector(selectCurrentHotelId)

  const newCampaigns = useCampaigns(isDefaultCampaigns)

  const allCampaigns = useSelector(s => s.campaigns.content)

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const currentHotel = useSelector(s => s.login.currentHotel)

  const userName = useSelector(s => s.login.currentUser.name)

  const history = useHistory()

  const contentSubsidiaryId = isDefaultCampaigns
    ? DEFAULT_SUBSIDIARY_ID
    : currentSubsidiaryId

  const { category, type } = useParams()

  const activeCampaigns = newCampaigns?.[category]

  useEffect(() => {
    dispatch(getCampaigns(contentSubsidiaryId)).then(() => {
      setIsLoading(false)
    })
  }, [contentSubsidiaryId, dispatch, history])

  const campaigns = allCampaigns[contentSubsidiaryId]

  const activeCampaign = campaigns?.find(c => c.category === category)

  const activeSecondaryCampaign = getSecondaryCampaign(campaigns, category)

  const { postEvent, plausibleEvents } = usePlausible()

  const handleSavePromo = (
    promotionCode,
    minNightsToStay,
    maxDiscountPercentage,
    promotionTagline
  ) => {
    dispatch(
      updateCampaign(contentSubsidiaryId, [activeCampaign.category], {
        promotionCode,
        minNightsToStay,
        maxDiscountPercentage,
        promotionTagline
      })
    )
    setIsEditingPromo(false)
  }

  const handleSaveCampaignTextAndMedia = (messageTemplate, campaignMedia) => {
    dispatch(
      updateCampaign(contentSubsidiaryId, [activeCampaign.category], {
        messageTemplate,
        messageMediaUrls: [campaignMedia]
      })
    )
    if (activeSecondaryCampaign) {
      dispatch(
        updateCampaign(contentSubsidiaryId, [activeSecondaryCampaign.category], {
          messageTemplate,
          messageMediaUrls: [campaignMedia]
        })
      )
    }

    history.goBack()
  }

  const handleSaveCampaignText = campaigns => {
    campaigns.forEach(({ category, messageTemplate, customUrl }) => {
      postEvent(plausibleEvents.UpdateCampaignContent, {
        category
      })

      const details = { messageTemplate }

      if (customUrl) {
        details.customUrl = customUrl
      }

      dispatch(updateCampaign(contentSubsidiaryId, [category], details))
    })

    history.goBack()
  }

  if (isEditingPromo) {
    return (
      <EditPromoDialog
        {...activeCampaign}
        maxDiscountPercentage={activeCampaign.maxDiscountPercentage || 5}
        handleSave={handleSavePromo}
        handleCloseDialog={() => setIsEditingPromo(false)}
      />
    )
  }

  if (!activeCampaign) {
    return <LoadingIndicator />
  }

  const campaignsLink = isDefaultCampaigns
    ? `/defaultCampaigns/${type}`
    : `/manageCampaigns/${type}`

  const canHaveImages = !isDefaultCampaigns && activeCampaign.kind === 'Service'

  function getTags(category) {
    if (category === 'OptInRequest') {
      return [
        {
          id: '__promoName__',
          display: 'promoName',
          length: activeCampaign.promotionTagline.length
        },
        {
          id: '__hotelName__',
          display: 'hotelName',
          length: 40
        }
      ]
    }

    if (
      ![
        'JustBooked',
        'Cancelled',
        'NoShow',
        'ServiceStartDayBefore',
        'ServiceStartForPrimaryGuest',
        'ServiceStartForSecondaryGuest',
        'ServiceRestart'
      ].includes(category)
    ) {
      return baseTags.filter(t => t.display !== 'checkinDate')
    }

    return baseTags
  }

  function getRequiredTagIds(category) {
    if (category.includes('Lost')) {
      return ['__hotelName__', '__guestName__', '__checkoutDate__']
    }

    if (category.includes('AfterLastVisit')) {
      return ['__hotelName__', '__guestName__', '__checkoutDate__']
    }

    if (category === 'OptInRequest') {
      return ['__hotelName__', '__promoName__']
    }

    if (category === 'AlreadySubscribed') {
      return ['__hotelName__']
    }

    return ['__hotelName__', '__guestName__']
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <EditCampaignContent
      title={`Editing text for ${getCampaignName(
        activeCampaign.category,
        isInternalAdmin
      )}`}
      campaigns={activeCampaigns}
      campaignMedia={activeCampaign.messageMediaUrls?.[0]}
      hasOptOutText={activeCampaign.kind === 'Sales'}
      hasLink={activeCampaign.kind === 'Sales'}
      primaryCampaignDefaultText={activeCampaign.messageTemplate}
      secondaryCampaignDefaultText={activeSecondaryCampaign?.messageTemplate}
      tags={getTags(category)}
      requiredTagIds={getRequiredTagIds(category)}
      canHaveImages={canHaveImages}
      goBackToCampaigns={() => history.push(campaignsLink)}
      onSaveText={canHaveImages ? handleSaveCampaignTextAndMedia : handleSaveCampaignText}
      category={activeCampaign.category}
      secondaryCategory={activeSecondaryCampaign?.category}
      contentId={activeCampaign.contentId}
      secondaryContentId={activeSecondaryCampaign?.contentId}
      primaryCampaignTitle={getPrimaryCampaignTitle(category)}
      secondaryCampaignTitle={getSecondaryCampaignTitle(category)}
      isDefaultCampaigns={isDefaultCampaigns}
      defaultData={{
        userName,
        hotelName: isDefaultCampaigns ? 'HotelName' : currentHotel,
        promotionName: activeCampaign.promotionTagline
      }}
      hasReviewLinkControl={activeCampaign.category === 'CheckedOutReview'}
      hasCustomLinkControl={activeCampaign.category === 'JustBooked'}
      customUrl={activeCampaign.customUrl}
      canBePublished={isDefaultCampaigns}
    />
  )
}

export default EditCampaign
